
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ListState } from '~/types/list';

@Component({
	name: 'ItemList'
})
export default class ItemList extends Vue {
	@Prop({ type: String }) readonly buttonText!: string;

	@Prop({
		type: String,
		validator: (value: string) =>
			['Article', 'Download', 'Event', 'Partner', 'Podcast'].includes(value)
	})
	readonly contentType?: string;

	@Prop({
		type: String,
		required: true,
		validator: (value: string) =>
			[
				'Article',
				'Download',
				'Event',
				'Partner',
				'Podcast',
				'Webinar',
				'TopCompany',
				'SimpleCompany',
				'Press',
				'Side'
			].includes(value)
	})
	readonly type!: string;

	@Prop({ type: Number, default: 0 }) readonly startAt!: number;

	@Prop({ type: Array, required: true }) readonly items!: [];

	@Prop({ type: Number, default: 6 }) readonly initialSize!: number;

	@Prop({ type: Number, default: 6 }) readonly itemsPerLoading!: number;

	@Prop({
		type: Function,
		default: () => false
	})
	readonly ssr!: (index: number) => boolean;

	@Prop({
		type: [Function, Boolean],
		required: false
	})
	readonly loadItems!: (count: number) => boolean | false;

	@Prop({
		type: String,
		default: 'tile',
		validator: (value: string) => ['list', 'tile', 'landingPage'].includes(value)
	})
	readonly styling!: string;

	@Prop({ type: Boolean, default: false }) readonly isCompanyPage?: boolean;

	// only show load button if loading is enabled and there are enough items when initializing the list
	// otherwise assume that there are not enough items anyway so we cant load more
	state =
		typeof this.loadItems === 'function' && this.items.length >= this.startAt + this.initialSize
			? ListState.Default
			: ListState.Finished;

	endAt = this.startAt + this.initialSize;

	contentItems = ['Article', 'Download', 'Event', 'Partner', 'Podcast'];

	data() {
		return {
			ListState
		};
	}

	get visibleItems() {
		return this.items.slice(this.startAt, this.endAt);
	}

	async showMore() {
		if (this.state === ListState.Loading || this.state === ListState.Finished) {
			return;
		}

		if (this.state === ListState.LastPage) {
			this.endAt = this.items.length;
			this.state = ListState.Finished;
		} else if (this.items.length >= this.endAt + this.itemsPerLoading) {
			this.endAt += this.itemsPerLoading;
		} else if (this.loadItems) {
			this.state = ListState.Loading;
			let hasMoreItems = false;
			try {
				hasMoreItems = await this.loadItems(this.items.length);
			} catch (err) {
				this.$errorHandler.call(this, err, 'Beim Laden ist ein Fehler aufgetreten.');
			}
			this.endAt = Math.min(this.endAt + this.itemsPerLoading, this.items.length);

			if (hasMoreItems) {
				this.state = ListState.Default;
			} else if (this.endAt === this.items.length) {
				this.state = ListState.Finished;
			} else {
				this.state = ListState.LastPage;
			}
		}
	}

	get classes() {
		switch (this.styling) {
			case 'list':
				return ['flex', 'flex-col'];
			case 'landingPage':
				return ['sm:grid', 'sm:grid-cols-3', 'sm:gap-4'];
			case 'tile':
			default:
				// return ['flex-wrap', 'justify-center'];
				return [
					'sm:grid',
					'sm:grid-cols-3',
					'sm:gap-4',
					'divide-y',
					'sm:divide-y-0',
					'divide-color-grey-light'
				];
		}
	}

	get isContentItem() {
		return this.contentItems.includes(this.type);
	}

	get currentContentType(): string | undefined {
		if (this.contentType) return this.contentType;
		if (this.isContentItem) return this.type;
		return undefined;
	}

	get currentComponent() {
		const itemName = this.isContentItem ? 'Content' : this.type;
		return () => import(`~/components/website/list/item/${itemName}Item.vue`);
	}
}
